import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Keep the LATEST `onEnter` / `onBack` reachable from a key listener whose
 * identity is deliberately stable.
 *
 * Why this exists: both hooks below bind ONE `keydown` listener and keep it
 * across renders (re-binding on every render would drop keys pressed mid-swap).
 * The callbacks are therefore captured in a closure — and a caller whose handler
 * reads component state (`onEnter: () => { if (phase === "ended") exit(); }`)
 * would forever see the state as it was on the render that bound the listener.
 * That is not theoretical: it shipped as "OK does nothing on the call-ended
 * card" (the closure still believed the call was ringing), while Back appeared
 * to work only because its handler exits unconditionally. Reading through a ref
 * gives every press the current callback without touching the listener.
 */
function useLatestHandlers<T>(
  onEnter?: (focus: T) => void,
  onBack?: () => void
) {
  const ref = useRef({ onEnter, onBack });
  useEffect(() => {
    ref.current = { onEnter, onBack };
  });
  return ref;
}

interface Args {
  rows: number;
  cols?: number;
  initial?: { r: number; c: number };
  scopeRef: RefObject<HTMLElement>;
  onEnter?: (focus: { r: number; c: number }) => void;
  onBack?: () => void;
  wrap?: boolean;
  enabled?: boolean;
}

/**
 * D-pad-friendly focus state for a 1-D list or 2-D grid. Ported from the
 * Claude Design tv-shared.jsx. Only fires while focus is inside `scopeRef`
 * so multiple schemes or screens never fight for the same key event.
 *
 * Returns the current focus + setter. The owner renders the focus ring.
 */
export function useFocusGrid({
  rows,
  cols = 1,
  initial = { r: 0, c: 0 },
  scopeRef,
  onEnter,
  onBack,
  wrap = true,
  enabled = true,
}: Args): [{ r: number; c: number }, (next: { r: number; c: number }) => void] {
  const [focus, setFocus] = useState(initial);
  const handlers = useLatestHandlers<{ r: number; c: number }>(onEnter, onBack);
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const el = scopeRef.current;
      if (!el) return;
      // Only respond when focus is inside the scope (or the scope itself
      // is focused - the auto-focus that useScreenFocus does).
      if (
        !el.contains(document.activeElement) &&
        document.activeElement !== el
      ) {
        return;
      }
      const k = e.key;
      if (
        ![
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Enter",
          " ",
          "Backspace",
          "Escape",
        ].includes(k)
      ) {
        return;
      }
      e.preventDefault();
      setFocus((prev) => {
        let { r, c } = prev;
        if (k === "ArrowUp")
          r = wrap ? (r - 1 + rows) % rows : Math.max(0, r - 1);
        if (k === "ArrowDown")
          r = wrap ? (r + 1) % rows : Math.min(rows - 1, r + 1);
        if (k === "ArrowLeft")
          c = wrap ? (c - 1 + cols) % cols : Math.max(0, c - 1);
        if (k === "ArrowRight")
          c = wrap ? (c + 1) % cols : Math.min(cols - 1, c + 1);
        return { r, c };
      });
      // Through the ref, never the closure - see useLatestHandlers.
      if (k === "Enter" || k === " ") handlers.current.onEnter?.(focus);
      if (k === "Backspace" || k === "Escape") handlers.current.onBack?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // `focus` is read by onEnter, so it stays a dep; the handlers do not, which
    // is the whole point of the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols, enabled, focus.r, focus.c, scopeRef, wrap]);
  return [focus, setFocus];
}

type NavDir = "up" | "down" | "left" | "right";
/** Adjacency map: tile id → the id you land on when pressing each direction.
 *  Omit a direction to make it a no-op (the focus stays put - a wall). */
export type NavMap = Record<string, Partial<Record<NavDir, string>>>;

interface NavArgs {
  map: NavMap;
  initial: string;
  scopeRef: RefObject<HTMLElement>;
  onEnter?: (id: string) => void;
  onBack?: () => void;
  enabled?: boolean;
}

/**
 * D-pad focus for an *asymmetric* layout (bento grids, mixed spans) where a
 * row/col model lies about what's actually next to what. Each tile names its
 * neighbour per direction; a missing direction is a wall. Same scoping rule as
 * useFocusGrid - only fires while focus is inside `scopeRef`.
 */
export function useFocusNav({
  map,
  initial,
  scopeRef,
  onEnter,
  onBack,
  enabled = true,
}: NavArgs): [string, (id: string) => void] {
  const [focus, setFocus] = useState(initial);
  const handlers = useLatestHandlers<string>(onEnter, onBack);
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const el = scopeRef.current;
      if (!el) return;
      if (
        !el.contains(document.activeElement) &&
        document.activeElement !== el
      ) {
        return;
      }
      const k = e.key;
      const dir: NavDir | null =
        k === "ArrowUp"
          ? "up"
          : k === "ArrowDown"
            ? "down"
            : k === "ArrowLeft"
              ? "left"
              : k === "ArrowRight"
                ? "right"
                : null;
      if (!dir && !["Enter", " ", "Backspace", "Escape"].includes(k)) {
        return;
      }
      e.preventDefault();
      if (dir) {
        setFocus((prev) => map[prev]?.[dir] ?? prev);
      }
      if (k === "Enter" || k === " ") handlers.current.onEnter?.(focus);
      if (k === "Backspace" || k === "Escape") handlers.current.onBack?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // focus is read by onEnter; map/enabled identity drives re-binding. The
    // handlers themselves come from the ref, so a stale closure cannot happen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, focus, scopeRef]);
  return [focus, setFocus];
}

/**
 * Make the scheme's root focusable + auto-focus it on pointer enter so the
 * useFocusGrid keydown listener has a target.
 */
export function useScreenFocus(ref: RefObject<HTMLElement>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.tabIndex = 0;
    function onPointerEnter() {
      if (!el) return;
      if (document.activeElement && el.contains(document.activeElement)) return;
      el.focus({ preventScroll: true });
    }
    el.addEventListener("pointerenter", onPointerEnter);
    // Also focus on mount so plain keyboard users (CMS preview popup) don't
    // have to hover first.
    el.focus({ preventScroll: true });
    return () => el.removeEventListener("pointerenter", onPointerEnter);
  }, [ref]);
}
